import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'height', 'heightUnit', 'waist', 'waistUnit',
    'unitImperial', 'unitMetric',
    'ratio', 'category', 'categoryBadge', 'resultContainer',
    'gaugeIndicator', 'targetWaist', 'insight',
    'catSlim', 'catHealthy', 'catOverweight', 'catObese'
  ]

  connect() {
    this.isMetric = false
    this.calculate()
  }

  setImperial() {
    if (!this.isMetric) return
    this.isMetric = false
    this.updateUnitDisplay()
    this.convertToImperial()
    this.calculate()
  }

  setMetric() {
    if (this.isMetric) return
    this.isMetric = true
    this.updateUnitDisplay()
    this.convertToMetric()
    this.calculate()
  }

  updateUnitDisplay() {
    if (this.isMetric) {
      this.unitMetricTarget.classList.add('bg-gray-900', 'text-white')
      this.unitMetricTarget.classList.remove('bg-white', 'text-gray-900')
      this.unitImperialTarget.classList.remove('bg-gray-900', 'text-white')
      this.unitImperialTarget.classList.add('bg-white', 'text-gray-900')

      this.heightUnitTarget.textContent = 'cm'
      this.waistUnitTarget.textContent = 'cm'
    } else {
      this.unitImperialTarget.classList.add('bg-gray-900', 'text-white')
      this.unitImperialTarget.classList.remove('bg-white', 'text-gray-900')
      this.unitMetricTarget.classList.remove('bg-gray-900', 'text-white')
      this.unitMetricTarget.classList.add('bg-white', 'text-gray-900')

      this.heightUnitTarget.textContent = 'in'
      this.waistUnitTarget.textContent = 'in'
    }
  }

  convertToMetric() {
    this.heightTarget.value = Math.round(parseFloat(this.heightTarget.value) * 2.54)
    this.waistTarget.value = Math.round(parseFloat(this.waistTarget.value) * 2.54)
  }

  convertToImperial() {
    this.heightTarget.value = Math.round(parseFloat(this.heightTarget.value) / 2.54)
    this.waistTarget.value = Math.round(parseFloat(this.waistTarget.value) / 2.54)
  }

  calculate() {
    const height = parseFloat(this.heightTarget.value) || 0
    const waist = parseFloat(this.waistTarget.value) || 0

    if (height <= 0 || waist <= 0) return

    // Calculate ratio (works regardless of units since we're dividing same units)
    const ratio = waist / height

    // Clamp to reasonable range
    const clampedRatio = Math.max(0.3, Math.min(0.9, ratio))

    // Update displays
    this.ratioTarget.textContent = clampedRatio.toFixed(2)

    // Determine category and update
    const category = this.getCategory(clampedRatio)
    this.categoryTarget.textContent = category.name
    this.updateCategoryBadge(category)
    this.updateResultContainer(category)
    this.highlightCategory(clampedRatio)

    // Update gauge
    this.updateGauge(clampedRatio)

    // Calculate target waist
    this.updateTargetWaist(height)

    // Update insight
    this.insightTarget.textContent = category.insight
  }

  getCategory(ratio) {
    if (ratio < 0.4) {
      return {
        name: 'SLIM',
        color: 'border-blue-500 text-blue-700',
        insight: 'Your WHtR is very low. While this indicates minimal abdominal fat, ensure you are ' +
          'maintaining adequate nutrition and muscle mass. Very low WHtR may indicate underweight status.'
      }
    }
    if (ratio < 0.5) {
      return {
        name: 'HEALTHY',
        color: 'border-green-500 text-green-700',
        insight: 'Excellent! Your WHtR is in the healthy range, indicating low cardiovascular and ' +
          'metabolic risk. Maintain your current lifestyle with regular exercise and balanced nutrition.'
      }
    }
    if (ratio < 0.6) {
      return {
        name: 'OVERWEIGHT',
        color: 'border-yellow-500 text-yellow-700',
        insight: 'Your WHtR suggests excess abdominal fat, associated with increased health risks. ' +
          'Consider focusing on cardiovascular exercise, strength training, and dietary improvements ' +
          'to reduce your waist circumference.'
      }
    }
    return {
      name: 'OBESE',
      color: 'border-red-500 text-red-700',
      insight: 'Your WHtR indicates significant abdominal obesity with elevated health risks ' +
        'including heart disease and diabetes. Consult a healthcare provider for a comprehensive ' +
        'assessment and personalized weight management plan.'
    }
  }

  updateCategoryBadge(category) {
    this.categoryBadgeTarget.className = `px-4 py-2 border-2 font-medium text-sm ${category.color}`
  }

  updateResultContainer(category) {
    const borderColor = category.color.split(' ')[0]
    this.resultContainerTarget.className = `p-8 bg-white border-2 text-center mb-6 ${borderColor}`
  }

  highlightCategory(ratio) {
    // Reset all
    const targets = [this.catSlimTarget, this.catHealthyTarget, this.catOverweightTarget, this.catObeseTarget]
    targets.forEach((t) => t.classList.remove('bg-gray-100', 'font-bold'))

    // Highlight current
    if (ratio < 0.4) {
      this.catSlimTarget.classList.add('bg-gray-100', 'font-bold')
    } else if (ratio < 0.5) {
      this.catHealthyTarget.classList.add('bg-gray-100', 'font-bold')
    } else if (ratio < 0.6) {
      this.catOverweightTarget.classList.add('bg-gray-100', 'font-bold')
    } else {
      this.catObeseTarget.classList.add('bg-gray-100', 'font-bold')
    }
  }

  updateGauge(ratio) {
    // Map ratio (0.3-0.8) to percentage (0-100)
    const percent = ((ratio - 0.3) / 0.5) * 100
    const clampedPercent = Math.max(0, Math.min(100, percent))
    this.gaugeIndicatorTarget.style.left = `${clampedPercent}%`
  }

  updateTargetWaist(height) {
    // Target WHtR is 0.5
    const targetWaist = height * 0.5
    const unit = this.isMetric ? 'cm' : 'in'
    this.targetWaistTarget.textContent = `<${Math.round(targetWaist)} ${unit}`
  }
}
